import { Badge, Card } from "@onemileauto/ui";
import { useAdminUsers } from "../../hooks/use-admin";
import { permissionLevelLabel } from "../../lib/permission-labels";

/**
 * Global accounts view (Phase 9) — "an admin can see all user accounts and
 * mailboxes" (the client's own framing for this pass). Every user in the
 * system (this is a deliberately single-tenant app — see architecture doc —
 * so that really does mean everyone) and every mailbox each one can access,
 * at what level. Complements, rather than replaces, the existing
 * per-mailbox ACL panels below (MailboxAclPanel) — those are where access
 * is actually granted/revoked one mailbox at a time; this is the one place
 * to see the whole picture across every mailbox at once.
 */
export function AccountsPanel() {
  const { data, isLoading, isError } = useAdminUsers();
  const users = data?.users ?? [];

  return (
    <Card className="p-4">
      <h2 className="font-display text-lg text-charcoal">All accounts</h2>
      <p className="mt-1 text-sm text-muted">Everyone who can sign in, and which mailboxes they can reach.</p>

      {isLoading ? (
        <p className="mt-3 text-sm text-muted">Loading&hellip;</p>
      ) : isError ? (
        <p className="mt-3 text-sm text-danger">Couldn&rsquo;t load accounts. Please try again.</p>
      ) : users.length === 0 ? (
        <p className="mt-3 text-sm text-muted">No accounts yet.</p>
      ) : (
        <ul className="mt-3 flex flex-col gap-2">
          {users.map((u) => (
            <li key={u.id} className="rounded-md border border-border bg-cream px-3 py-2.5">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                <p className="text-sm font-medium text-charcoal">{u.displayName}</p>
                <p className="text-xs text-muted">
                  {u.email}
                  {u.username ? ` · @${u.username}` : ""}
                </p>
              </div>
              {u.access.length === 0 ? (
                <p className="mt-1.5 text-xs text-muted">No mailbox access</p>
              ) : (
                <ul className="mt-1.5 flex flex-wrap gap-1.5">
                  {u.access.map((a) => (
                    <li key={a.mailboxId}>
                      <Badge variant={a.permissionLevel === "owner" ? "default" : "outline"}>
                        {a.mailboxAddress} — {permissionLevelLabel(a.permissionLevel)}
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
